"use client";

import { useState } from "react";
import Image from "next/image";
import { DZTypes } from "@dealerzone/libs";
import { Gallery, Item } from "react-photoswipe-gallery";
import "photoswipe/dist/photoswipe.css";

interface PswpImage {
  thumbnail: string;
  src: string;
  w: number;
  h: number;
}

interface ImageGalleryProps {
  vehicle: DZTypes.Vehicle & { pswp?: string };
}

export default function ImageGallery({ vehicle }: ImageGalleryProps) {
  const images = vehicle.images || [];
  const [selectedIndex, setSelectedIndex] = useState(0);

  const pswpImages: PswpImage[] = vehicle.pswp ? JSON.parse(vehicle.pswp) : [];

  const mainImage = images[selectedIndex]?.large || vehicle.main_image?.large || "/images/placeholder.jpg";

  const getRibbon = () => {
    if (vehicle.is_sold) {
      return { text: "SOLD", color: "bg-red-600" };
    }
    if (vehicle.is_on_hold) {
      return { text: "Sale Pending", color: "bg-amber-500" };
    }
    if (vehicle.is_repaired) {
      return { text: "Ready To Go", color: "bg-green-600" };
    }
    return null;
  };

  const ribbon = getRibbon();

  return (
    <Gallery>
      <div className="space-y-3">
        <div className="relative aspect-image bg-zinc-100 rounded-xl overflow-hidden">
          {pswpImages.length > 0 ? (
            pswpImages.map((img, index) => (
              <Item
                key={index}
                original={img.src}
                thumbnail={img.thumbnail}
                width={img.w}
                height={img.h}
              >
                {({ ref, open }) => (
                  <div
                    ref={ref}
                    onClick={open}
                    className={`absolute inset-0 cursor-zoom-in ${index === selectedIndex ? "block" : "hidden"}`}
                  >
                    <Image
                      src={images[index]?.large || mainImage}
                      alt={`${vehicle.name} - Image ${index + 1}`}
                      fill
                      className="object-cover"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      priority={index === 0}
                    />
                  </div>
                )}
              </Item>
            ))
          ) : (
            <Image
              src={mainImage}
              alt={vehicle.name}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 50vw"
              priority
            />
          )}
          {ribbon && (
            <div className={`absolute top-4 left-4 px-3 py-1.5 ${ribbon.color} text-white text-sm font-semibold rounded-lg shadow-lg z-10`}>
              {ribbon.text}
            </div>
          )}
          {images.length > 1 && (
            <div className="absolute bottom-4 right-4 px-2 py-1 bg-black/60 text-white text-xs rounded z-10">
              {selectedIndex + 1} / {images.length}
            </div>
          )}
          {pswpImages.length > 0 && (
            <div className="absolute bottom-4 left-4 px-2 py-1 bg-black/60 text-white text-xs rounded flex items-center gap-1 z-10">
              <i className="bootstrap-zoom-in"></i>
              Click to enlarge
            </div>
          )}
        </div>

        {images.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setSelectedIndex(index)}
                className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                  index === selectedIndex ? "border-red-600" : "border-transparent hover:border-zinc-300"
                }`}
              >
                <Image
                  src={image.small || image.medium || image.large}
                  alt={`${vehicle.name} - Image ${index + 1}`}
                  fill
                  className="object-cover"
                  sizes="80px"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </Gallery>
  );
}
